import React, { FC, useState } from 'react';
import { Lock } from '@mui/icons-material';
import { Dialog } from '@mui/material';
import { useMachine } from '@xstate/react';
import FileSaver from 'file-saver';
import { useNavigate } from 'react-router-dom';
import { ClipLoader } from 'react-spinners';
import { toast } from 'react-toastify';
import { createMachine } from 'xstate';
import API from 'api/API';
import Button from 'components/Button';
import ConfirmModal from 'components/ConfirmModal/ConfirmModal';
import { IS_DEMO_APP } from 'components/Navigation/Navigation.constants';
import PageSpinner from 'components/PageSpinner/PageSpinner';
import RestrictedComponent from 'components/RestrictedComponent/RestrictedComponent';
import SetModal from 'components/SetModal/SetModal';
import EditDraftModal from 'components/SetModal/Workflows/EditDraftModal';
import { useLanguage } from 'context/AppContext';
import { SetAction } from 'reducers/DraftReducer/draftActions';
import { approverRoles, entryRoles, reviewerRoles } from 'utils/helpers/permissions.helpers';
import { createApprovalWorkflowMachineConfig, helpers, TContext, TStates } from '../machines/ApprovalWorkflow/machine';
import { SetButtons } from '../SetPage.styles';

interface SetActionButtonsProps extends TContext {
  updateSet: React.Dispatch<SetAction>;
}

const SetActionButtons: FC<SetActionButtonsProps> = ({ currentUser, setDraft, updateSet }) => {
  const [language] = useLanguage();
  const navigate = useNavigate();

  // const [state, send]
  const approvalWorkflowMachine = useMachine(
    createMachine(
      createApprovalWorkflowMachineConfig(
        {
          currentUser,
          setDraft,
        },
        navigate,
      ),
    ),
    {
      // @ts-expect-error
      devTools: process.env.REACT_APP_NODE_ENV === 'development' && !window.Cypress,
    },
  );
  const [state, send] = approvalWorkflowMachine;

  const [pictureListLoading, setPictureListLoading] = useState<boolean>(false);

  const getPictureList = async () => {
    setPictureListLoading(true);
    try {
      if (setDraft.is_view_only) {
        const pdfSetURI = await API.set.getSetPDF(setDraft.code);
        FileSaver.saveAs(pdfSetURI, `${setDraft.display_code}_SET_PICTURE_LIST`);
      } else {
        const pdfDraftURI = await API.set.getDraftPDF(setDraft.code);
        FileSaver.saveAs(pdfDraftURI, `${setDraft.display_code}_DRAFT_PICTURE_LIST`);
      }
    } catch (e) {
      toast.error(language.DraftActions.pictureListError);
    }
    setPictureListLoading(false);
  };

  const showLoadingBackdrop =
    state.matches({ DELETING: 'DELETING' }) ||
    state.matches({ DRAFT: 'SAVING' }) ||
    state.matches({ DRAFT: 'SUBMITTING' }) ||
    state.matches({ REVIEW: 'REJECTING' }) ||
    state.matches({ REVIEW: 'APPROVING' }) ||
    state.matches({ REVIEW: 'MERGING' }) ||
    state.matches({ PENDING: 'REJECTING' }) ||
    state.matches({ PENDING: 'MERGING' });

  return (
    <>
      <SetButtons>
        {!IS_DEMO_APP && (
          <Button onClick={() => getPictureList()}>
            {!pictureListLoading ? language.DraftActions.getPictureList : <ClipLoader color={'white'} size={25} />}
          </Button>
        )}
        {setDraft.is_view_only ? (
          <RestrictedComponent allowedRoles={entryRoles}>
            <Button onClick={() => send('EDIT_DRAFT')} disabled={setDraft.draft_exists}>
              {setDraft.draft_exists && <Lock fontSize="small" />}
              {language.DraftActions.create}
            </Button>
            <Button
              onClick={() => navigate(`/set/${setDraft.code}/manage-serial-numbers`)}
              disabled={setDraft.draft_exists}
            >
              {setDraft.draft_exists && <Lock fontSize="small" />}
              {language.DraftActions.manageSerialNumbers}
            </Button>
          </RestrictedComponent>
        ) : (
          <>
            {state.matches<TStates>('DRAFT') && (
              <>
                {helpers.canSubmitDraft({
                  currentUser,
                  setDraft,
                }) && (
                  <Button onClick={() => send('SUBMIT_DRAFT', { set: setDraft })}>
                    {language.DraftActions.submit}
                  </Button>
                )}
                {helpers.canMergeDraft({ currentUser, setDraft }) && (
                  <Button
                    onClick={() => {
                      send('MERGE_DRAFT_REVIEW', { set: setDraft });
                    }}
                  >
                    {language.DraftActions.merge}
                  </Button>
                )}
                {helpers.canApproveDraft({ currentUser, setDraft }) && (
                  <Button
                    onClick={() => {
                      send('APPROVE_DRAFT_REVIEW', { set: setDraft });
                    }}
                  >
                    {language.DraftActions.approve}
                  </Button>
                )}
                <Button onClick={() => send('SAVE_DRAFT', { set: setDraft })}>{language.DraftActions.save}</Button>
                <Button onClick={() => send('ADD_TRAY')}>{language.DraftActions.add}</Button>
                <Button onClick={() => send('DELETE')}>{language.DraftActions.delete}</Button>
              </>
            )}

            {state.matches<TStates>('REVIEW') && (
              <>
                <RestrictedComponent allowedRoles={reviewerRoles}>
                  <Button onClick={() => send('APPROVE_DRAFT_REVIEW', { set: setDraft })}>
                    {language.DraftActions.approve}
                  </Button>
                  <Button onClick={() => send('REJECT_DRAFT_REVIEW')}>{language.DraftActions.reject}</Button>
                </RestrictedComponent>
                {helpers.customerCanMerge({ currentUser, setDraft }) && (
                  <Button onClick={() => send('MERGE_DRAFT_REVIEW', { set: setDraft })}>
                    {language.DraftActions.merge}
                  </Button>
                )}
                {helpers.isDraftOwnerOrAdmin({ currentUser, setDraft }) && (
                  <Button onClick={() => send('DELETE')}>{language.DraftActions.delete}</Button>
                )}
              </>
            )}

            {state.matches<TStates>('PENDING') && (
              <>
                <RestrictedComponent allowedRoles={approverRoles}>
                  <Button onClick={() => send('MERGE_DRAFT_PENDING', { set: setDraft })}>
                    {language.DraftActions.merge}
                  </Button>
                  <Button onClick={() => send('REJECT_DRAFT_PENDING')}>{language.DraftActions.reject}</Button>
                </RestrictedComponent>
                {helpers.isDraftOwnerOrAdmin({ currentUser, setDraft }) && (
                  <Button onClick={() => send('DELETE')}>{language.DraftActions.delete}</Button>
                )}
              </>
            )}
          </>
        )}
      </SetButtons>

      <SetModal
        open={state.matches({ DRAFT: 'ADDING' })}
        close={() => send('CONFIRM_ADD_TRAY')}
        draft={setDraft}
        dispatchDraft={updateSet}
      />
      <EditDraftModal
        open={state.matches({ DRAFT: 'EDITING' })}
        close={() => send('CONFIRM_EDIT_DRAFT')}
        setCode={setDraft.code}
        titleText={language.EditDraftModal.setTitle}
        bodyText={language.EditDraftModal.setMessage}
        type={'SET'}
        url={`/set/${setDraft.code}/edit`}
      />
      <ConfirmModal
        open={state.matches({ DELETING: 'IDLE' })}
        close={() => send('CANCEL_DELETE')}
        confirm={() => send('CONFIRM_DELETE')}
        confirmText={language.DraftActions.confirmDelete}
        buttonText={language.DraftActions.delete}
      />
      <Dialog open={showLoadingBackdrop}>
        <PageSpinner />
      </Dialog>
    </>
  );
};

export default SetActionButtons;
